import {
	Badge,
	type BadgeProps,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { toast } from "sonner";

import {
	layoutStore,
	useLayoutStore,
} from "AdvisoryManagement/Stores/useLayoutStore";
import type { SimplifiedExtractedDocumentResponse } from "Infrastructure/Api/Api";

export const DocumentExtractModal = () => {
	const documentExtractModalOpen = useLayoutStore(
		(s) => s.documentExtractModalOpen,
	);
	const extractedData = useLayoutStore((s) => s.documentExtractData);
	const closeDocumentExtractModal = useLayoutStore(
		(s) => s.closeDocumentExtractModal,
	);

	return (
		<Dialog
			open={documentExtractModalOpen}
			onClose={closeDocumentExtractModal}
			maxWidth="md"
			fullWidth
		>
			<DialogTitle>Zkontrolujte data nahraného dokumentu</DialogTitle>
			<DialogContent>
				{extractedData ? (
					<>
						<Typography variant="body2" color="textSecondary">
							Extrahovaná data dokumentu:
						</Typography>
						<Box
							sx={{
								maxWidth: "100%",
								maxHeight: 400,
								overflow: "auto",
								backgroundColor: "#f5f5f5",
							}}
						>
							<pre>{JSON.stringify(extractedData || "", null, 2)}</pre>
						</Box>
					</>
				) : (
					<Typography>Nahrávání...</Typography>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={closeDocumentExtractModal}>Zrušit</Button>
				<Button variant="contained" onClick={closeDocumentExtractModal}>
					Potvrdit údaje
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export const notifyDocumentExtractionReady = (
	jobId: string,
	doc: SimplifiedExtractedDocumentResponse,
) => {
	const { openDocumentExtractModal, markNotified } = layoutStore.get();

	toast.success(
		`Dokument “${doc.documentInfo.product.metadata.productName.message || doc.documentInfo.product.productCode || doc.documentInfo.product.productCode}” je připraven`,
		{
			id: jobId,
			duration: Infinity,
			closeButton: true,
			action: (
				<Button
					variant="text"
					color="success"
					onClick={() => {
						openDocumentExtractModal(doc);
						toast.dismiss(jobId);
					}}
					sx={{ marginLeft: "20px" }}
				>
					Zobrazit detaily
				</Button>
			),
			style: { maxWidth: "600px", width: "max-content" },
			onDismiss: () => markNotified(jobId),
			onAutoClose: () => markNotified(jobId),
		},
	);
};

type ActiveUploadsBadgeProps = {
	badgeProps?: BadgeProps;
	children?: ReactNode;
};

export const ActiveUploadsBadge = ({
	children,
	badgeProps,
}: ActiveUploadsBadgeProps) => {
	const activeCount = useLayoutStore(
		(s) =>
			Object.values(s.jobs).filter(
				(j) => j.status === "pending" || j.status === "processing",
			).length,
	);

	return (
		<Badge
			invisible={activeCount === 0}
			badgeContent={<CircularProgress size={12} thickness={5} />}
			sx={{
				"& .MuiBadge-badge": {
					p: 0,
					minWidth: 16,
					height: 16,
					borderRadius: "50%",
				},
			}}
			{...badgeProps}
		>
			{children}
		</Badge>
	);
};
